'use client';

/**
 * Login Page
 * Authentication form with Zod validation
 * Handles session expired messages and redirect after login
 */

import { z } from 'zod';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState, Suspense, FormEvent } from 'react';
import Image from 'next/image';
import { AlertCircle } from 'lucide-react';

// Login form validation schema
const loginSchema = z.object({
  email: z
    .string()
    .min(1, 'El correo electrónico es requerido')
    .email('Correo electrónico inválido'),
  password: z.string().min(1, 'La contraseña es requerida'),
});

type LoginFormData = z.infer<typeof loginSchema>;
type FormErrors = Partial<Record<keyof LoginFormData, string>>;

// Inner component that uses useSearchParams
const isDemoMode = process.env.NEXT_PUBLIC_API_BASE_URL === '/api';

function LoginForm() {
  const { login, isAuthenticated, isLoading: authLoading } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Form state
  const [formData, setFormData] = useState<LoginFormData>({ email: '', password: '' });
  const [errors, setErrors] = useState<FormErrors>({});

  // Check for session expired and redirect params
  const sessionExpired = searchParams.get('session') === 'expired';
  const redirectTo = searchParams.get('redirect') || '/';

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      setIsRedirecting(true);
      router.replace(redirectTo);
    }
  }, [isAuthenticated, authLoading, router, redirectTo]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name as keyof LoginFormData]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Validate with zod
    const result = loginSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: FormErrors = {};
      result.error.issues.forEach((issue) => {
        const field = issue.path[0] as keyof LoginFormData;
        if (!fieldErrors[field]) {
          fieldErrors[field] = issue.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      await login(result.data);
      // Store redirect path for after 2FA verification
      if (redirectTo !== '/') {
        sessionStorage.setItem('auth_redirect', redirectTo);
      }
      // Redirect to 2FA verification page after successful credential validation
      router.replace('/login/verify');
    } catch (error) {
      console.error('Login failed:', error);
      setIsSubmitting(false);
    }
  };

  // Show loading state while checking authentication or redirecting
  if (authLoading || isRedirecting) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="text-brand-accent text-xl">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
      <div className="w-full max-w-md">
        {/* Logo Section */}
        <div className="mb-8 text-center">
          <div className="mb-6 flex justify-center">
            <div className="rounded-xl bg-white px-6 py-3 shadow-lg">
              <Image
                src="/images/naka-logo-nobg.png"
                alt="NAKA Electrónica"
                width={200}
                height={70}
                priority
                className="h-auto w-auto"
              />
            </div>
          </div>
          <h1 className="mb-2 text-3xl font-bold text-white">Panel de Análisis de Riesgos</h1>
          <p className="text-slate-400">Ingresa tus credenciales para continuar</p>
        </div>

        {/* Session Expired Alert */}
        {sessionExpired && (
          <div className="mb-6 flex items-center gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-amber-200">
            <AlertCircle className="h-5 w-5 flex-shrink-0" />
            <p className="text-sm">Tu sesión ha expirado. Por favor, inicia sesión nuevamente.</p>
          </div>
        )}

        {/* Login Card */}
        <div className="rounded-2xl border border-slate-700/50 bg-slate-800/80 p-8 shadow-2xl backdrop-blur-sm">
          {isDemoMode && (
            <div className="mb-5 rounded-lg border border-blue-500/30 bg-blue-500/10 px-4 py-3 text-sm text-blue-200">
              <strong>Modo Demo:</strong> Usa el correo{' '}
              <span className="font-mono font-semibold">admin@demo.com</span> con cualquier
              contraseña.
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email Field */}
            <div>
              <label htmlFor="email" className="mb-2 block text-sm font-medium text-slate-300">
                Correo Electrónico
              </label>
              <input
                id="email"
                name="email"
                type="email"
                placeholder="usuario@example.com"
                value={formData.email}
                onChange={handleChange}
                disabled={isSubmitting}
                className={`focus:ring-brand-accent/40 focus:border-brand-accent w-full rounded-lg border bg-slate-900/50 px-4 py-3 text-white placeholder-slate-500 transition-all duration-200 focus:ring-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 ${errors.email ? 'border-red-500' : 'border-slate-700'}`}
              />
              {errors.email && <p className="mt-1.5 text-sm text-red-400">{errors.email}</p>}
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="mb-2 block text-sm font-medium text-slate-300">
                Contraseña
              </label>
              <input
                id="password"
                name="password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={handleChange}
                disabled={isSubmitting}
                className={`focus:ring-brand-accent/40 focus:border-brand-accent w-full rounded-lg border bg-slate-900/50 px-4 py-3 text-white placeholder-slate-500 transition-all duration-200 focus:ring-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 ${errors.password ? 'border-red-500' : 'border-slate-700'}`}
              />
              {errors.password && <p className="mt-1.5 text-sm text-red-400">{errors.password}</p>}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="bg-brand-lime hover:bg-brand-lime-light focus:ring-brand-lime/50 w-full rounded-lg px-4 py-3 font-semibold text-slate-900 transition-all duration-200 focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-800 focus:outline-none active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting ? 'Iniciando sesión...' : 'Iniciar Sesión'}
            </button>
          </form>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-slate-500">
          <p>© {new Date().getFullYear()} NAKA Electrónica. Todos los derechos reservados.</p>
        </div>
      </div>
    </div>
  );
}

// Main component with Suspense for useSearchParams
export default function LoginPage() {
  return (
    <Suspense
      fallback={
        <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
          <div className="text-brand-accent text-xl">Cargando...</div>
        </div>
      }
    >
      <LoginForm />
    </Suspense>
  );
}
